/**
 * Block Foundation - Main TypeScript Entry Point
 * Handles interactive elements, navigation, and animations
 */

// =============================================================================
// Types
// =============================================================================

interface NavigationState {
    isOpen: boolean;
    isScrolled: boolean;
}

// =============================================================================
// State
// =============================================================================

const state: NavigationState = {
    isOpen: false,
    isScrolled: false,
};

// =============================================================================
// DOM Elements
// =============================================================================

const getElements = () => ({
    header: document.querySelector('.header') as HTMLElement | null,
    menuToggle: document.querySelector('.header__menu-toggle') as HTMLElement | null,
    mobileNav: document.querySelector('.mobile-nav') as HTMLElement | null,
    navLinks: document.querySelectorAll('a[href^="#"]') as NodeListOf<HTMLAnchorElement>,
});

// =============================================================================
// Navigation
// =============================================================================

/**
 * Toggle mobile navigation
 */
const toggleMobileNav = () => {
    const { mobileNav, menuToggle } = getElements();

    state.isOpen = !state.isOpen;

    if (mobileNav) {
        mobileNav.classList.toggle('mobile-nav--open', state.isOpen);
    }

    if (menuToggle) {
        menuToggle.setAttribute('aria-expanded', String(state.isOpen));
    }

    // Prevent body scroll when menu is open
    document.body.style.overflow = state.isOpen ? 'hidden' : '';
};

/**
 * Close mobile navigation
 */
const closeMobileNav = () => {
    const { mobileNav, menuToggle } = getElements();

    state.isOpen = false;

    if (mobileNav) {
        mobileNav.classList.remove('mobile-nav--open');
    }

    if (menuToggle) {
        menuToggle.setAttribute('aria-expanded', 'false');
    }

    document.body.style.overflow = '';
};

/**
 * Handle scroll events for header styling
 */
const handleScroll = () => {
    const { header } = getElements();
    const scrollY = window.scrollY;

    state.isScrolled = scrollY > 50;

    if (header) {
        header.classList.toggle('header--scrolled', state.isScrolled);
    }
};

// =============================================================================
// Smooth Scroll
// =============================================================================

/**
 * Smooth scroll to anchor links
 */
const initSmoothScroll = () => {
    const { navLinks } = getElements();

    navLinks.forEach((link) => {
        link.addEventListener('click', (e) => {
            const href = link.getAttribute('href');

            if (href && href.startsWith('#') && href.length > 1) {
                const target = document.querySelector(href);

                if (target) {
                    e.preventDefault();

                    const headerHeight = document.querySelector('.header')?.offsetHeight || 0;
                    const targetPosition = target.getBoundingClientRect().top + window.scrollY - headerHeight;

                    window.scrollTo({
                        top: targetPosition,
                        behavior: 'smooth',
                    });

                    // Close mobile nav if open
                    closeMobileNav();
                }
            }
        });
    });
};

// =============================================================================
// Animations
// =============================================================================

/**
 * Initialize intersection observer for scroll animations
 */
const initScrollAnimations = () => {
    const animatedElements = document.querySelectorAll('[data-animate], .animate-on-scroll, .animate-fade-in, .animate-slide-left, .animate-slide-right');

    if (animatedElements.length === 0) return;

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('animate-in', 'is-visible');
                    observer.unobserve(entry.target);
                }
            });
        },
        {
            threshold: 0.1,
            rootMargin: '0px 0px -50px 0px',
        }
    );

    animatedElements.forEach((el) => observer.observe(el));
};

/**
 * Initialize feature card animations on scroll
 */
const initCardAnimations = () => {
    const featureCards = document.querySelectorAll('.feature-card, .logo-card');

    if (featureCards.length === 0) return;

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const card = entry.target as HTMLElement;
                    const index = Array.from(featureCards).indexOf(card);
                    card.style.animationDelay = `${index * 0.1}s`;
                    card.classList.add('is-visible');
                    observer.unobserve(card);
                }
            });
        },
        {
            threshold: 0.1,
            rootMargin: '0px 0px -30px 0px',
        }
    );

    featureCards.forEach((card) => observer.observe(card));
};

// =============================================================================


// =============================================================================
// Theme Toggle
// =============================================================================

type Theme = 'dark' | 'light';

/**
 * Get the current theme from localStorage or system preference
 */
const getStoredTheme = (): Theme => {
    const stored = localStorage.getItem('theme') as Theme | null;
    if (stored) return stored;

    // Default to dark; ignore system preference
    return 'dark';
};

/**
 * Set the theme on the document
 */
const setTheme = (theme: Theme) => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
};

/**
 * Toggle between dark and light themes
 */
const toggleTheme = () => {
    const current = document.documentElement.getAttribute('data-theme') || 'dark';
    const newTheme: Theme = current === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
};

/**
 * Initialize theme toggle functionality
 */
const initThemeToggle = () => {
    // Apply stored/preferred theme on load
    const initialTheme = getStoredTheme();
    setTheme(initialTheme);

    // Set up toggle button
    const themeToggle = document.querySelector('.theme-toggle');
    if (themeToggle) {
        themeToggle.addEventListener('click', toggleTheme);
    }
};

// =============================================================================
// Language Toggle
// =============================================================================

type Language = 'en' | 'nl';

/**
 * Get the current language
 */
const getStoredLanguage = (): Language => {
    const stored = localStorage.getItem('language') as Language | null;
    if (stored) return stored;

    // Check browser language
    const browserLang = navigator.language.toLowerCase();
    if (browserLang.startsWith('nl')) return 'nl';
    return 'en';
};

/**
 * Set the active language
 */
const setLanguage = (lang: Language) => {
    document.documentElement.setAttribute('lang', lang);
    localStorage.setItem('language', lang);

    // Update UI
    const langOptions = document.querySelectorAll('.lang-toggle__option');
    langOptions.forEach((option) => {
        const optionLang = option.getAttribute('data-lang');
        option.classList.toggle('lang-toggle__option--active', optionLang === lang);
    });
};

/**
 * Read the value of a cookie by name (used for Django CSRF).
 */
const getCookie = (name: string): string | null => {
    const match = document.cookie
        .split(';')
        .map((c) => c.trim())
        .find((c) => c.startsWith(`${name}=`));
    return match ? decodeURIComponent(match.split('=')[1]) : null;
};

/**
 * Persist the locale on the Django backend by POSTing to /i18n/setlang/.
 * Reloads the page on success so server-rendered translations apply.
 */
const persistLanguageOnServer = (lang: Language) => {
    const form = new FormData();
    form.append('language', lang);
    form.append('next', window.location.pathname + window.location.search);
    const csrfToken = getCookie('csrftoken');
    fetch('/i18n/setlang/', {
        method: 'POST',
        body: form,
        credentials: 'same-origin',
        headers: csrfToken ? { 'X-CSRFToken': csrfToken } : {},
    })
        .then(() => {
            window.location.reload();
        })
        .catch(() => {
            // Silently ignore — UI state is still updated client-side.
        });
};

/**
 * Initialize language toggle functionality
 */
const initLanguageToggle = () => {
    // Apply stored/preferred language on load
    const initialLang = getStoredLanguage();
    setLanguage(initialLang);

    // Set up language option clicks
    const langOptions = document.querySelectorAll('.lang-toggle__option');
    langOptions.forEach((option) => {
        option.addEventListener('click', (e) => {
            e.stopPropagation();
            const lang = (option.getAttribute('data-lang') || 'en') as Language;
            if (lang === getStoredLanguage()) return;
            setLanguage(lang);
            persistLanguageOnServer(lang);
        });
    });
};

// =============================================================================
// Initialization
// =============================================================================

/**
 * Initialize all interactive features
 */
const init = () => {
    const { menuToggle } = getElements();

    // Initialize navigation
    if (menuToggle) {
        menuToggle.addEventListener('click', toggleMobileNav);
    }

    // Initialize scroll handling
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial state

    // Initialize smooth scroll
    initSmoothScroll();

    // Initialize animations
    initScrollAnimations();
    initCardAnimations();

    // Initialize theme toggle
    initThemeToggle();

    // Initialize language toggle
    initLanguageToggle();

    console.log('Block Foundation website initialized');
};

// Run initialization when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}

export { closeMobileNav, init, toggleMobileNav };

